import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Snackbar,
  Autocomplete,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { LoaderWrapper } from '../../components/index';
import { 
  getCategoryList, 
  addCategory, 
  updateCategory, 
  deleteCategory,
  getStandardsByField 
} from '../../services/api';

const CategoryManagement = () => {
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [standards, setStandards] = useState([]);
  
  // Filter state
  const [filters, setFilters] = useState({
    field: '',
    standard: '',
    displayId: '',
    categoryName: '',
  });
  
  // Form state
  const [formData, setFormData] = useState({
    displayId: '',
    categoryName: '',
    field: '0',
    standard: '',
  });
  
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  
  // Dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  
  // Form validation errors
  const [errors, setErrors] = useState({});

  const fieldTypes = [
    { value: '0', label: 'Select Field' },
    { value: 'Testing', label: 'Testing' },
    { value: 'Medical', label: 'Medical' },
    { value: 'Calibration', label: 'Calibration' },
    { value: 'PTP', label: 'PTP' },
    { value: 'RMP', label: 'RMP' },
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  // Apply filters whenever categories or filters change
  useEffect(() => {
    applyFilters();
  }, [categories, filters]);

  useEffect(() => {
    if (formData.field && formData.field !== '0') {
      fetchStandards(formData.field);
    } else {
      setStandards([]);
      setFormData(prev => ({ ...prev, standard: '' }));
    }
  }, [formData.field]);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await getCategoryList();
      if (response.data.success) {
        setCategories(response.data.data || []);
        setFilteredCategories(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      showSnackbar('Error fetching categories', 'error');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...categories];

    // Filter by field
    if (filters.field) {
      filtered = filtered.filter(cat => 
        cat.Field === filters.field
      );
    }

    // Filter by standard
    if (filters.standard) {
      filtered = filtered.filter(cat => 
        cat.Standard === filters.standard
      );
    }

    // Filter by display id
    if (filters.displayId) {
      filtered = filtered.filter(cat => 
        (cat.DisId || cat.DisplayId) === filters.displayId
      );
    }

    // Filter by category name
    if (filters.categoryName) {
      filtered = filtered.filter(cat => 
        cat.CatName === filters.categoryName
      );
    }

    setFilteredCategories(filtered);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      field: '',
      standard: '',
      displayId: '',
      categoryName: '',
    });
  };

  // Get unique values for filter dropdowns
  const getUniqueFields = () => {
    const fields = [...new Set(categories.map(cat => cat.Field).filter(Boolean))];
    return fields;
  };

  const getUniqueStandards = () => {
    let standards = [...new Set(categories.map(cat => cat.Standard).filter(Boolean))];
    
    // If field filter is active, only show standards for that field
    if (filters.field) {
      standards = [...new Set(
        categories
          .filter(cat => cat.Field === filters.field)
          .map(cat => cat.Standard)
          .filter(Boolean)
      )];
    }
    
    return standards;
  };

  const getUniqueDisplayIds = () => {
    const ids = [...new Set(categories.map(cat => cat.DisId || cat.DisplayId).filter(Boolean))];
    return ids;
  };

  const getUniqueCategoryNames = () => {
    const names = [...new Set(categories.map(cat => cat.CatName).filter(Boolean))];
    return names;
  };

  const fetchStandards = async (field) => {
    try {
      const response = await getStandardsByField(field);
      if (response.data.success) {
        setStandards(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching standards:', error);
      showSnackbar('Error fetching standards', 'error');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.field || formData.field === '0') {
      newErrors.field = 'Field Type is required';
    }
    
    if (!formData.standard) {
      newErrors.standard = 'Standard is required';
    }
    
    if (!formData.displayId.trim()) {
      newErrors.displayId = 'Display Id is required';
    }
    
    if (!formData.categoryName.trim()) {
      newErrors.categoryName = 'Category Name is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      const payload = {
        displayId: formData.displayId.trim(),
        disId: formData.displayId.trim(),
        catName: formData.categoryName.trim(),
        field: formData.field,
        standard: formData.standard,
      };
      
      const response = await addCategory(payload);
      
      if (response.data.success) {
        showSnackbar('Category added successfully', 'success');
        resetForm();
        fetchCategories();
      } else {
        showSnackbar(response.data.message || 'Error adding category', 'error');
      }
    } catch (error) {
      console.error('Error adding category:', error);
      const errorMessage = error.response?.data?.message || 'Error adding category';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setLoading(true);
      
      const payload = {
        id: editId,
        displayId: formData.displayId.trim(),
        disId: formData.displayId.trim(),
        catName: formData.categoryName.trim(),
        field: formData.field,
        standard: formData.standard,
      };
      
      const response = await updateCategory(payload);
      
      if (response.data.success) {
        showSnackbar('Category updated successfully', 'success');
        resetForm();
        fetchCategories();
      } else {
        showSnackbar(response.data.message || 'Error updating category', 'error');
      }
    } catch (error) {
      console.error('Error updating category:', error);
      const errorMessage = error.response?.data?.message || 'Error updating category';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category) => {
    setFormData({
      displayId: category.DisId || category.DisplayId || '',
      categoryName: category.CatName || '',
      field: category.Field || '0',
      standard: category.Standard || '',
    });
    setEditMode(true);
    setEditId(category.Id || category.id);
    
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      const response = await deleteCategory(deleteId);
      
      if (response.data.success) {
        showSnackbar('Category deleted successfully', 'success');
        fetchCategories();
      } else {
        showSnackbar(response.data.message || 'Error deleting category', 'error');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      const errorMessage = error.response?.data?.message || 'Error deleting category';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteId(null);
  };

  const resetForm = () => {
    setFormData({
      displayId: '',
      categoryName: '',
      field: '0',
      standard: '',
    });
    setEditMode(false);
    setEditId(null);
    setErrors({});
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return <LoaderWrapper loading={true} type="page" message="Loading..." />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#00024c' }}>
        {editMode ? 'Edit Category' : 'Add Category'}
      </Typography>

      {/* Form Section */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <form onSubmit={editMode ? handleUpdate : handleSubmit}>
          <Grid container spacing={3}>
            {/* Field Type */}
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                error={!!errors.field}
                required
              >
                <InputLabel>Field Type</InputLabel>
                <Select
                  name="field"
                  value={formData.field}
                  onChange={handleInputChange}
                  label="Field Type"
                >
                  {fieldTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
                {errors.field && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.field}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Standard */}
            <Grid item xs={12} md={6}>
              <FormControl 
                fullWidth 
                error={!!errors.standard}
                required
                disabled={!formData.field || formData.field === '0'}
              >
                <InputLabel>Standard</InputLabel>
                <Select
                  name="standard"
                  value={formData.standard}
                  onChange={handleInputChange}
                  label="Standard"
                >
                  <MenuItem value="">
                    <em>Select Standard</em>
                  </MenuItem>
                  {standards.map((std) => (
                    <MenuItem key={std.Id || std.id} value={std.standard || std.Standard}>
                      {std.standard || std.Standard}
                    </MenuItem>
                  ))}
                </Select>
                {errors.standard && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                    {errors.standard}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {/* Display Id */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Display Id"
                name="displayId"
                value={formData.displayId}
                onChange={handleInputChange}
                error={!!errors.displayId}
                helperText={errors.displayId}
              />
            </Grid>

            {/* Category Name */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                label="Category Name"
                name="categoryName"
                value={formData.categoryName}
                onChange={handleInputChange}
                error={!!errors.categoryName}
                helperText={errors.categoryName}
              />
            </Grid>

            {/* Buttons */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color={editMode ? 'error' : 'primary'}
                  disabled={loading}
                >
                  {editMode ? 'Update' : 'Submit'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={resetForm}
                  disabled={loading}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Table Section */}
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          backgroundColor: '#e0ebeb',
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Categories List
          </Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchCategories}
            size="small"
            variant="outlined"
          >
            Refresh
          </Button>
        </Box>

        {/* Filter Section */}
        <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderBottom: '1px solid #e0e0e0' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <FilterListIcon sx={{ mr: 1, color: '#666' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#666' }}>
                  Filter Categories
                </Typography>
              </Box>
            </Grid>
            
            {/* Field Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                options={getUniqueFields()}
                value={filters.field || null}
                onChange={(event, newValue) => {
                  setFilters(prev => ({
                    ...prev,
                    field: newValue || '',
                  }));
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Field" 
                    placeholder="Search or select field"
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                clearOnEscape
              />
            </Grid>

            {/* Standard Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                options={getUniqueStandards()}
                value={filters.standard || null}
                onChange={(event, newValue) => {
                  setFilters(prev => ({
                    ...prev,
                    standard: newValue || '',
                  }));
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Standard" 
                    placeholder="Search or select standard"
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                clearOnEscape
              />
            </Grid>

            {/* Display Id Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                options={getUniqueDisplayIds()}
                value={filters.displayId || null}
                onChange={(event, newValue) => {
                  setFilters(prev => ({
                    ...prev,
                    displayId: newValue || '',
                  }));
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Display Id" 
                    placeholder="Search or select display id"
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                clearOnEscape
              />
            </Grid>

            {/* Category Name Filter */}
            <Grid item xs={12} sm={6} md={3}>
              <Autocomplete
                size="small"
                options={getUniqueCategoryNames()}
                value={filters.categoryName || null}
                onChange={(event, newValue) => {
                  setFilters(prev => ({
                    ...prev,
                    categoryName: newValue || '',
                  }));
                }}
                renderInput={(params) => (
                  <TextField 
                    {...params} 
                    label="Category Name" 
                    placeholder="Search or select category"
                  />
                )}
                isOptionEqualToValue={(option, value) => option === value}
                clearOnEscape
              />
            </Grid>

            {/* Clear Filters Button */}
            <Grid item xs={12}>
              <Button
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                size="small"
                variant="outlined"
                color="secondary"
              >
                Clear All Filters
              </Button>
              <Typography variant="caption" sx={{ ml: 2, color: '#666' }}>
                Showing {filteredCategories.length} of {categories.length} categories
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Edit</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Delete</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Field</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Standard</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Display Id</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>Category Name</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCategories.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {categories.length === 0 ? 'No categories found' : 'No categories match the selected filters'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredCategories.map((category) => (
                  <TableRow
                    key={category.Id || category.id}
                    hover
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell align="center">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleEdit(category)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDeleteClick(category.Id || category.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                    <TableCell align="center">{category.Field || 'N/A'}</TableCell>
                    <TableCell align="center">{category.Standard || 'N/A'}</TableCell>
                    <TableCell align="center">{category.DisId || category.DisplayId || 'N/A'}</TableCell>
                    <TableCell align="center">{category.CatName || 'N/A'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this category?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CategoryManagement;
